import { getAuthApiBasePath } from '@/lib/constants/api';

/**
 * Client-side seam for this app's own /api/auth/* routes — the browser mirror of
 * lib/auth/providers/*. Centralizes the base path, cookie forwarding, and the
 * `{ ok, status, body }` response shape so call sites don't re-derive them.
 */

/** Never throws on an HTTP error — callers branch on `ok`/`status` instead. */
export type AuthApiResult = {
  ok: boolean;
  status: number;
  body: Record<string, unknown>;
};

// A non-JSON or empty body resolves to {} so callers can always read `body`.
async function authFetch(path: string, init?: RequestInit): Promise<AuthApiResult> {
  const res = await fetch(`${getAuthApiBasePath()}${path}`, { credentials: 'include', ...init });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body: body ?? {} };
}

/**
 * Pull a human-readable message out of an /api/auth/* error body. Only `message` is read
 * since every route's failures go through authError(), which emits exactly `{ message }`
 * (string or the validation layer's string[]). Falls back to `fallback` otherwise.
 */
export function authErrorMessage(body: unknown, fallback: string): string {
  const { message } = (body && typeof body === 'object' ? body : {}) as { message?: unknown };
  const msg = typeof message === 'string' ? message : Array.isArray(message) ? message.join(', ') : undefined;
  return msg?.trim() || fallback;
}

/** Builds a `?a=b&c=d` query string, dropping the need to hand-roll URLSearchParams per call site. */
function query(params: Record<string, string>): string {
  return `?${new URLSearchParams(params).toString()}`;
}

function postJson(path: string, data?: unknown): Promise<AuthApiResult> {
  return authFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: data === undefined ? undefined : JSON.stringify(data),
  });
}

/** Always resolves 200; read `body.sessionStatus` for the verdict (see app/api/auth/session/route.ts). */
export function fetchSession(): Promise<AuthApiResult> {
  return authFetch('/session');
}

/** Email/password sign-in. A 400 here is how a partner-only account surfaces. */
export function loginLocal(credentials: { email: string; password: string }): Promise<AuthApiResult> {
  return postJson('/local/login', credentials);
}

/** Sign up; on success the user is already signed in (session cookies are set). */
export function registerLocal(input: {
  firstName?: string;
  lastName?: string;
  email: string;
  role?: string;
  company?: string;
  password: string;
}): Promise<AuthApiResult> {
  return postJson('/local/register', input);
}

/** Ends the server session only; client-side state is cleared by useLogout()/utils/auth/common/logout.ts. */
export function logoutRequest(): Promise<AuthApiResult> {
  return postJson('/logout');
}

/** Exchange the Contentstack OIDC `?code=` for a session (422 body signals needs-registration). */
export function exchangeSsoCode(params: { region: string; code: string; redirectUri: string }): Promise<AuthApiResult> {
  return authFetch(`/sso/callback${query({ region: params.region, code: params.code, redirect_uri: params.redirectUri })}`);
}

/** Complete SSO sign-in after a 422 by supplying company/role; relays the resulting Set-Cookie. */
export function completeSsoRegistration(
  data: { role: string; company: string },
  params: { region: string; redirectUri: string }
): Promise<AuthApiResult> {
  return postJson(`/sso/register${query({ region: params.region ?? '', redirect_uri: params.redirectUri ?? '' })}`, data);
}

/** "Email me a reset link" — always resolves ok, since the route answers 200 for any address to avoid an account-existence oracle. */
export function requestPasswordReset(email: string): Promise<AuthApiResult> {
  return postJson('/local/reset-request', { email });
}

/** Second half of the reset: exchange the emailed token for a new password. Unlike requestPasswordReset, this one reports failure — branch on `ok`/`status`. */
export function resetPasswordWithToken(data: { token: string; password: string }): Promise<AuthApiResult> {
  return postJson('/local/reset', data);
}
